import React from 'react';
import styled from 'styled-components';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

const DAYS = [
    { value: 1, label: 'Seg' },
    { value: 2, label: 'Ter' },
    { value: 3, label: 'Qua' },
    { value: 4, label: 'Qui' },
    { value: 5, label: 'Sex' },
    { value: 6, label: 'Sáb' },
    { value: 7, label: 'Dom' },
];

export default function StepSchedule({ data, onChange }) {
    const schedule = data.schedule;

    function setSchedule(key, value) {
        onChange({ ...data, schedule: { ...schedule, [key]: value } });
    }

    function toggleDay(day) {
        const current = schedule.days_of_week || [];
        const next = current.includes(day)
            ? current.filter(d => d !== day)
            : [...current, day].sort((a, b) => a - b);
        setSchedule('days_of_week', next);
    }

    const isOneTime = schedule.schedule_type === 'one_time';

    return (
        <Container>
            <Field>
                <Label>Tipo de agendamento *</Label>
                <ScheduleTypeRow>
                    <TypeOption
                        type='button'
                        $selected={!isOneTime}
                        onClick={() => setSchedule('schedule_type', 'recurring')}
                    >
                        <TypeDot $selected={!isOneTime} />
                        <div>
                            <TypeLabel>Recorrente</TypeLabel>
                            <TypeDesc>Repete em dias e horários fixos</TypeDesc>
                        </div>
                    </TypeOption>
                    <TypeOption
                        type='button'
                        $selected={isOneTime}
                        onClick={() => setSchedule('schedule_type', 'one_time')}
                    >
                        <TypeDot $selected={isOneTime} />
                        <div>
                            <TypeLabel>Uma vez</TypeLabel>
                            <TypeDesc>Período com data e hora específica</TypeDesc>
                        </div>
                    </TypeOption>
                </ScheduleTypeRow>
            </Field>

            {isOneTime ? (
                <TwoCol>
                    <Field>
                        <Label>Início *</Label>
                        <Input
                            type='datetime-local'
                            value={schedule.starts_at}
                            onChange={e => setSchedule('starts_at', e.target.value)}
                        />
                    </Field>
                    <Field>
                        <Label>Fim *</Label>
                        <Input
                            type='datetime-local'
                            value={schedule.ends_at}
                            min={schedule.starts_at || undefined}
                            onChange={e => setSchedule('ends_at', e.target.value)}
                        />
                    </Field>
                </TwoCol>
            ) : (
                <>
                    <Field>
                        <Label>Dias da semana *</Label>
                        <DayPicker>
                            {DAYS.map(day => {
                                const active = (schedule.days_of_week || []).includes(day.value);
                                return (
                                    <DayChip
                                        key={day.value}
                                        type='button'
                                        $active={active}
                                        onClick={() => toggleDay(day.value)}
                                    >
                                        {day.label}
                                    </DayChip>
                                );
                            })}
                        </DayPicker>
                    </Field>

                    <TwoCol>
                        <Field>
                            <Label>Horário de início *</Label>
                            <Input
                                type='time'
                                value={schedule.time_start}
                                onChange={e => setSchedule('time_start', e.target.value)}
                            />
                        </Field>
                        <Field>
                            <Label>Horário de fim *</Label>
                            <Input
                                type='time'
                                value={schedule.time_end}
                                min={schedule.time_start || undefined}
                                onChange={e => setSchedule('time_end', e.target.value)}
                            />
                        </Field>
                    </TwoCol>

                    <Fieldset>
                        <FieldsetLegend>Período de vigência (opcional)</FieldsetLegend>
                        <TwoCol>
                            <Field>
                                <Label>Válido de</Label>
                                <Input
                                    type='date'
                                    value={schedule.valid_from}
                                    onChange={e => setSchedule('valid_from', e.target.value)}
                                />
                            </Field>
                            <Field>
                                <Label>Válido até</Label>
                                <Input
                                    type='date'
                                    value={schedule.valid_until}
                                    min={schedule.valid_from || undefined}
                                    onChange={e => setSchedule('valid_until', e.target.value)}
                                />
                            </Field>
                        </TwoCol>
                    </Fieldset>
                </>
            )}
        </Container>
    );
}

const Container = styled.div`display: flex; flex-direction: column; gap: 20px;`;

const Field = styled.div`display: flex; flex-direction: column; gap: 6px;`;

const Label = styled.label`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Input = styled.input`
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 14px;
    outline: none;
    color: ${colors.nightDark};
    background: #fff;
    transition: border-color 0.15s;
    &:focus { border-color: ${panelAccent(colors.occasionalPurple)}; }
`;

const TwoCol = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
`;

const ScheduleTypeRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
`;

const TypeOption = styled.button`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px;
    border-radius: 12px;
    border: 1.5px solid ${({ $selected }) => $selected ? panelAccent(colors.occasionalPurple) : colors.neutralDark};
    background: ${({ $selected }) => $selected ? `${panelAccentAlpha(colors.occasionalPurple, '0D')}` : '#fff'};
    cursor: pointer;
    text-align: left;
    transition: all 0.15s;
    &:hover { border-color: ${panelAccentAlpha(colors.occasionalPurple, '40')}; }
`;

const TypeDot = styled.div`
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid ${({ $selected }) => $selected ? panelAccent(colors.occasionalPurple) : colors.nightLight};
    background: ${({ $selected }) => $selected ? panelAccent(colors.occasionalPurple) : 'transparent'};
    flex-shrink: 0;
    margin-top: 2px;
    transition: all 0.15s;
    position: relative;
    &::after {
        content: '';
        position: absolute;
        inset: 2px;
        border-radius: 50%;
        background: ${({ $selected }) => $selected ? '#fff' : 'transparent'};
    }
`;

const TypeLabel = styled.span`
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const TypeDesc = styled.span`
    display: block;
    font-size: 11px;
    color: ${colors.nightLight};
    margin-top: 2px;
    line-height: 1.3;
`;

const DayPicker = styled.div`
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
`;

const DayChip = styled.button`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    border: 1.5px solid ${({ $active }) => $active ? panelAccent(colors.occasionalPurple) : colors.neutralDark};
    background: ${({ $active }) => $active ? panelAccent(colors.occasionalPurple) : '#fff'};
    color: ${({ $active }) => $active ? '#fff' : colors.night};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    flex-shrink: 0;
    &:hover { border-color: ${panelAccent(colors.occasionalPurple)}; }
`;

const Fieldset = styled.div`
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const FieldsetLegend = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${colors.nightLight};
`;
